// THE confirmation dialog (app.md §8). Every "Are you sure?" in the modal
// action bars is this one component: Deny, Archive, Move To Manual Entry, Hold,
// Release Hold, Reinstate, and the approve confirmations. For bulk, the same
// single comment applies to every selected invoice (the body text says so).
//
// ONE LAYOUT FOR ALL OF THEM (owner, 2026-09-25: "verify that its structure
// matches the Deny, Archive, and Move To Manual Entry pop-ups"). The Archive
// dialog used to be its own component, `NotAnInvoiceDialog`, with a flex
// column and a 12px gap, while this one stacked its parts with ad hoc margins
// — so the same three parts sat at different distances in different dialogs.
// That component is gone: the editor's Archive renders this one, and every
// part here sits in one column, with the body paragraph's margin removed so
// nothing adds a gap of its own.
//
// THE POP-UP FORM SPACING (D-349): the parts sit 16px apart — the sentence,
// the Category, the Comment — and the buttons 24px below the last, the
// standard `dialogForm.ts` holds. They were 12px apart, with the buttons 8px
// below, until then.
import { useEffect, useState } from 'react'
import {
  Button,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  MessageBar,
  MessageBarBody,
  Option,
  Textarea,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components'
import { AppDialog } from './AppDialog'
import { REASON_CATEGORY_OTHER, reasonCategoryLabel } from '../lib/constants'
import { FrozenWhileClosed } from './FrozenWhileClosed'
import { useDialogFormStyles } from './dialogForm'

interface Props {
  open: boolean
  title: string
  /** Confirmation prose, e.g. "Approve 12 invoices?". */
  body?: string
  /** When true, the reason box renders and is part of this dialog's answer.
      Whether it is REQUIRED depends on the category — see `categories`. */
  requireReason: boolean
  /** The free-text box's label. *Comment* on every pop-up (owner, 2026-10-08,
      D-326: "change Reason to Comment"); it read *Reason* until then. Copy
      only — the stored field and the API field are still `reason`. */
  reasonLabel?: string
  /** Placeholder for the reason box (e.g. an example reason). */
  reasonPlaceholder?: string
  /** THE CATEGORY VOCABULARY FOR THIS DIALOG, in the order it is offered
      (owner, 2026-09-15). Present on Deny, Archive, Move To Manual Entry and
      Hold, and the values differ on each — one question per action. Absent
      means this dialog asks for no category at all.

      When present the dropdown renders FIRST, above the reason box ("Put
      Category above Reason"), and is REQUIRED: a decision has to be countable,
      and prose is not. The reason box below it is then required only when the
      pick is `other` — "make the Reason required only if Other is chosen as the
      category" — because a named category IS the explanation and `other` is the
      one that isn't. */
  categories?: readonly string[]
  /** How a category key reads in the dropdown. `reasonCategoryLabel` unless a
      dialog's keys are not reason categories — Denial Review's Archive, whose
      two answers ARE the two stored archive outcomes. */
  categoryLabel?: (key: string) => string
  /** The category already picked when the dialog opens — the duplicate flag's
      Archive Invoice opens on *Duplicate* (QA C1-06). The reviewer can still
      change it; the Reason follows the picked category's rule as usual. */
  defaultCategory?: string
  /** The reason is required whatever the category (Denial Review's Archive
      keeps its long-standing rule: the reviewer says why, every time). */
  reasonAlwaysRequired?: boolean
  confirmLabel: string
  destructive?: boolean
  /** Extra class for the confirm button (e.g. the green approve fill). */
  confirmClassName?: string
  /** Optional caution note (e.g. the "approval commits — no undo" warning). */
  note?: string
  submitting?: boolean
  onConfirm: (reason: string, category?: string) => void
  onCancel: () => void
}

const useStyles = makeStyles({
  // ONE LINE PER BUTTON, WHATEVER THE LABEL (QA C2-03, 2026-09-25). Fluent
  // parks `DialogActions` in the right two thirds of the body's grid
  // (`grid-column: 2 / 4`), so *Move To Manual Entry* and *Manually Entered
  // Into Point Click Care* ran out of room, wrapped onto two lines, and Cancel
  // grew to the same double height. `fluid` gives the footer the whole row
  // (it still sits at the right), and no button may wrap or shrink: a long
  // label widens the footer instead.
  button: { whiteSpace: 'nowrap', flexShrink: 0 },
})

export function DecisionDialog({
  open,
  title,
  body,
  requireReason,
  reasonLabel = 'Comment',
  reasonPlaceholder = 'Required — visible in the decision history.',
  categories,
  categoryLabel = reasonCategoryLabel,
  defaultCategory,
  reasonAlwaysRequired = false,
  confirmLabel,
  destructive,
  confirmClassName,
  note,
  submitting,
  onConfirm,
  onCancel,
}: Props) {
  const s = useStyles()
  const f = useDialogFormStyles()
  const [reason, setReason] = useState('')
  // The picked category KEY (e.g. 'price_discrepancy'), or '' for none.
  const [reasonCategory, setReasonCategory] = useState(defaultCategory ?? '')

  useEffect(() => {
    if (open) {
      setReason('')
      setReasonCategory(defaultCategory ?? '')
    }
  }, [open, defaultCategory])

  // The reason is required on a dialog that asks for one AND either asks for no
  // category at all (Reinstate), always wants prose (Denial Review's Archive),
  // or has `other` picked.
  const categoryAnswers = !!categories
  const reasonRequired =
    requireReason && (!categoryAnswers || reasonAlwaysRequired || reasonCategory === REASON_CATEGORY_OTHER)
  const canConfirm =
    !submitting &&
    (!categoryAnswers || reasonCategory !== '') &&
    (!reasonRequired || reason.trim().length > 0)

  return (
    <AppDialog open={open} onOpenChange={(_, d) => { if (!d.open && !submitting) onCancel() }}>
      {/* ALWAYS DIMMED (2026-09-25). Fluent draws no backdrop behind a dialog
          opened from inside another one, so Hold — which opens from inside the
          invoice modal — showed the page undimmed while Deny, opened from the
          screen, dimmed it. One pop-up, one look, wherever it is mounted. */}
      {/* NAMED BY ITS TITLE (QA 2026-09-30, C1-06). Fluent points the
          surface's `aria-labelledby` at the `DialogTitle`, but the tester's
          reading of the page found these pop-ups unnamed beside the invoice
          modal, which carries an `aria-label`. The same words go on as an
          `aria-label` here, so the name does not depend on that pairing. */}
      <DialogSurface backdrop={{ appearance: 'dimmed' }} aria-label={title}>
        {/* NOTHING FADES ANY MORE (D-239, QA 2026-09-30 C1-10). This latch
            (QA wave B, 2026-09-25, B-13; D-169) kept *Submitting…* on a
            surface that went on rendering through its exit animation, after
            the caller had cleared `submitting` in the render that closed it.
            `AppDialog` now unmounts the surface in that same render, so the
            latched body is never drawn. Left in place because it is inert:
            while `open` is true it renders its children unchanged. */}
        <FrozenWhileClosed open={open}><DialogBody>
          <DialogTitle>{title}</DialogTitle>
          {/* Every part 16px apart and the buttons 24px below, nothing else
              adding space: the one layout the Deny, Archive, Move To Manual
              Entry and Hold dialogs share. */}
          <DialogContent className={f.beforeActions}>
            <div className={f.stack}>
              {body && <p style={{ margin: 0 }}>{body}</p>}
              {/* CATEGORY FIRST (owner, 2026-09-15: "Put Category above Reason").
                  It is the answer the decision is counted by, and on every pick
                  but `other` it is the whole answer — so asking for prose above it
                  put the optional question first. */}
              {categories && (
                <Field label="Category" required>
                  <Dropdown
                    placeholder="Pick a category"
                    selectedOptions={reasonCategory ? [reasonCategory] : []}
                    value={reasonCategory ? categoryLabel(reasonCategory) : ''}
                    onOptionSelect={(_, d) => setReasonCategory(d.optionValue ?? '')}
                  >
                    {categories.map((key) => (
                      <Option key={key} value={key} text={categoryLabel(key)}>
                        {categoryLabel(key)}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              )}
              {/* NO HINT UNDER THE REASON FIELD (owner, 2026-09-15: "Drop the
                  help text ... I never asked for this. Drop any other such help
                  text that you added to the Move To Manual Entry, Not An Invoice,
                  and Deny pop-ups"). The asterisk that appears the moment Other
                  is picked says it. The PLACEHOLDER stays — it is an example of
                  a reason, which the owner asked for on 2026-09-14. */}
              {requireReason && (
                <Field label={reasonLabel} required={reasonRequired}>
                  <Textarea
                    value={reason}
                    onChange={(_, d) => setReason(d.value)}
                    placeholder={reasonPlaceholder}
                    resize="vertical"
                    rows={3}
                  />
                </Field>
              )}
              {note && (
                <MessageBar intent="warning">
                  <MessageBarBody>{note}</MessageBarBody>
                </MessageBar>
              )}
            </div>
          </DialogContent>
          <DialogActions fluid>
            <Button appearance="secondary" className={s.button} onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              className={mergeClasses(s.button, confirmClassName)}
              // Red only while it can be pressed: an inline fill overrides
              // Fluent's disabled grey, so a disabled Deny or Archive used to
              // look ready to click.
              style={destructive && canConfirm ? { backgroundColor: '#D13438' } : undefined}
              disabled={!canConfirm}
              onClick={() => onConfirm(reason.trim(), reasonCategory || undefined)}
            >
              {submitting ? 'Submitting…' : confirmLabel}
            </Button>
          </DialogActions>
        </DialogBody></FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}
